import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

const ScrollableDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  disableScrollLock = true,
  dividers = false,
  showCloseButton = false,
  hideCloseOnXs = false,
  paperBorderRadius = '17px',
  paperSx,
  titleSx,
  contentSx,
  actionsSx,
  dialogTitleProps,
  dialogContentProps,
  dialogActionsProps,
  ...dialogProps
}) => {
  const canClose = typeof onClose === 'function';
  const hasHeader = Boolean(title || subtitle);
  const hasActions = Boolean(actions);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableScrollLock={disableScrollLock}
      PaperProps={{
        sx: {
          borderRadius: paperBorderRadius,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          ...paperSx,
        },
      }}
      {...dialogProps}
    >
      {title || subtitle ? (
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: subtitle ? 'flex-start' : 'center',
            gap: showCloseButton && canClose ? 2 : 0,
            px: { xs: 2, sm: 3 },
            pb: subtitle ? 1 : 2,
            borderTopLeftRadius: paperBorderRadius,
            borderTopRightRadius: paperBorderRadius,
            ...titleSx,
          }}
          {...dialogTitleProps}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            {typeof title === 'string' ? (
              <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {title}
              </Typography>
            ) : title}
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {showCloseButton && canClose ? (
            <IconButton
              onClick={onClose}
              size="small"
              sx={hideCloseOnXs ? { display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 } : { flexShrink: 0 }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
      ) : null}

      <DialogContent
        dividers={dividers}
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          backgroundColor: 'background.paper',
          ...(!hasHeader && {
            borderTopLeftRadius: paperBorderRadius,
            borderTopRightRadius: paperBorderRadius,
          }),
          ...(!hasActions && {
            borderBottomLeftRadius: paperBorderRadius,
            borderBottomRightRadius: paperBorderRadius,
          }),
          ...contentSx,
        }}
        {...dialogContentProps}
      >
        {children}
      </DialogContent>

      {actions ? (
        <DialogActions
          disableSpacing
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            gap: 1,
            borderBottomLeftRadius: paperBorderRadius,
            borderBottomRightRadius: paperBorderRadius,
            ...actionsSx,
          }}
          {...dialogActionsProps}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default ScrollableDialog;